import { Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';

import {
  injectSiteBudget,
  injectUpdateSiteBudgetMutation,
} from '@models/sites/api/site-budget.api';

import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { listSiteBudgetVersionsQuery } from '@shared/services/gql.service';

import { uniqSiteBudgetNameValidator } from './validators/uniq-site-budget-name.validator';

export type EditSiteBudgetNameModalData = {
  budgetVersion: listSiteBudgetVersionsQuery;
};

@Component({
  imports: [InputComponent, ReactiveFormsModule],
  selector: 'aux-edit-site-budget-name-modal',
  template: `
    <div class="w-[350px]">
      <aux-input
        #siteBudgetNameInput
        [formControl]="siteBudgetName"
        label="Site Budget Amendment"
        [validators]="validators"
      />
    </div>
  `,
})
export class EditSiteBudgetNameModalComponent implements OnInit {
  ref = inject(CustomOverlayRef<unknown, { budgetVersion: listSiteBudgetVersionsQuery }>);

  siteBudget = injectSiteBudget({ site_id: signal(this.ref.data?.budgetVersion.site_id) });
  updateSiteBudgetMutation = injectUpdateSiteBudgetMutation(true);

  siteBudgetNameInput = viewChild<InputComponent>('siteBudgetNameInput');

  siteBudgetName = new FormControl('');
  validators: ValidatorFn[] = [
    Validators.required,
    uniqSiteBudgetNameValidator(this.siteBudget.data, this.ref.data?.budgetVersion.name),
  ];

  ngOnInit(): void {
    this.siteBudgetName.setValue(this.ref.data?.budgetVersion.name);
  }

  async updateSiteAmendmentName() {
    this.siteBudgetName.updateValueAndValidity();
    this.siteBudgetNameInput()?.fc.markAsTouched();
    this.siteBudgetNameInput()?.fc.updateValueAndValidity();

    if (this.siteBudgetName.invalid) {
      return;
    }

    try {
      await this.updateSiteBudgetMutation.mutateAsync({
        id: this.ref.data?.budgetVersion.id ?? '',
        name: this.siteBudgetName.value ?? '',
        site_id: this.ref.data?.budgetVersion.site_id ?? '',
      });
      this.ref.close();
    } catch (error: unknown) {
      console.error(error);
      this.siteBudgetNameInput()?.fc.markAsTouched();
      this.siteBudgetNameInput()?.fc.updateValueAndValidity();
      this.siteBudgetNameInput()?.fc.setErrors({
        duplicate_site_budget: true,
      });
    }
  }
}
